import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import { Event, Router, RouterEvent, Scroll } from '@angular/router';
import { Location } from '@angular/common';
import { AuthenticationService } from '@src/app/_services/authentication.service';
import { TitleService } from '@src/app/_services/title.service';
import { MessageService, NotificationMessage } from '@src/app/_services/message.service';
import { VersionCheckService } from '@src/app/_services/version-check.service';
import { Observable } from 'rxjs';
import { AutologoutService } from '@src/app/_services/autologout.service';
import {
    NodeClickEventArgs,
    SidebarComponent,
    TreeViewComponent,
} from '@syncfusion/ej2-angular-navigations';
import { enableRipple, L10n, loadCldr, setCulture, setCurrencyCode } from '@syncfusion/ej2-base';
import { NavHandlerService } from '@src/app/_services/nav-handler.service';
import { User } from '@src/app/_models/user/user';

import numbers from 'cldr-data/main/cs/numbers.json';
import gregorian from 'cldr-data/main/cs/ca-gregorian.json';
import numberingSystems from 'cldr-data/supplemental/numberingSystems.json';
import timeZoneNames from 'cldr-data/main/cs/timeZoneNames.json';
import weekData from 'cldr-data/supplemental/weekData.json';
import units from 'cldr-data/main/cs/units.json';
import layout from 'cldr-data/main/cs/layout.json';
import measurementSystemNames from 'cldr-data/main/cs/measurementSystemNames.json';
import { DialogComponent } from '@syncfusion/ej2-angular-popups';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MenuEventArgs } from '@syncfusion/ej2-splitbuttons';
import { DropDownButtonComponent, ItemModel } from '@syncfusion/ej2-angular-splitbuttons';
import { TextBoxComponent } from '@syncfusion/ej2-angular-inputs';
import { map, tap } from 'rxjs/operators';
import csLocale from '@syncfusion/ej2-locale/src/cs.json';
import { EnvironmentService } from '@src/app/_services/environment.service';
import { Settings } from 'luxon';
import { ToastComponent } from '@syncfusion/ej2-angular-notifications';
import { LogoutService } from '@src/app/_services/logout.service';
import { BreakpointObserver } from '@angular/cdk/layout';

loadCldr(
    numbers,
    gregorian,
    numberingSystems,
    timeZoneNames,
    weekData,
    units,
    layout,
    measurementSystemNames
);
enableRipple(true);
L10n.load(csLocale);

setCulture('cs');
setCurrencyCode('CZK');
Settings.defaultLocale = 'cz';
Settings.defaultNumberingSystem = 'latn';
Settings.defaultZone = 'Europe/Prague';

interface NodeChild {
    nodeId: string;
    nodeText: string;
    iconCss: string;
    url: string;
    acl: boolean;
}

interface NavData {
    nodeId: string;
    nodeText: string;
    iconCss: string;
    acl: boolean;
    url?: string;
    badge?: string;
    message?: number | string;
    nodeChild?: Array<NodeChild>;
}

@UntilDestroy()
@Component({
    selector: 'app-root',
    templateUrl: 'app.component.html',
    styleUrls: ['app.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
    private readonly specialPages: string[] = ['/login', '/'];

    mobile$?: Observable<boolean>;

    timeoutId: NodeJS.Timeout | number | string | undefined;

    // Notifications
    position = { X: 'Right', Y: 'Top' };

    notification?: NotificationMessage;

    // Variables
    name = 'JON Sync portal';

    currentUser: User | null;

    specialPage: boolean;

    isLogedIn = false;

    currentUrl = '';

    width = '250px';

    cssClass = 'accordiontree';

    mediaQuery = '(min-width: 600px)';

    target = '.main-content';

    data: NavData[] = [];

    field = {
        dataSource: this.data,
        id: 'nodeId',
        text: 'nodeText',
        child: 'nodeChild',
        iconCss: 'iconCss',
    };

    items: ItemModel[] = [
        {
            text: 'Profil',
        },
        {
            text: 'Odhlásit',
        },
    ];

    renderSearchResultDialog = false;

    searchQuery = '';

    autoLogoutDialogState = false;

    // Menus
    @ViewChild('userMenuIcon') userMenuIcon: DropDownButtonComponent | undefined;

    @ViewChild('sidebarTreeviewInstance')
    sidebarTreeviewInstance: SidebarComponent | undefined;

    @ViewChild('treeMenuInstance') treeMenuInstance: TreeViewComponent | undefined;

    // Dialogs
    @ViewChild('notifDialog') notifDialog: DialogComponent | undefined;

    @ViewChild('autoLogoutDialog') autoLogoutDialog: DialogComponent | undefined;

    // Search
    @ViewChild('fulltextSearch') fullTextSearchObj: TextBoxComponent | undefined;

    @ViewChild('notifElement') notifElement: ToastComponent;

    constructor(
        public router: Router,
        public messageService: MessageService,
        protected autologoutService: AutologoutService,
        private readonly location: Location,
        private readonly authenticationService: AuthenticationService,
        private readonly navHandlerService: NavHandlerService,
        private readonly titleService: TitleService,
        private readonly versionCheckService: VersionCheckService,
        private readonly logoutService: LogoutService,
        private readonly environmentService: EnvironmentService,
        private readonly changeDetector: ChangeDetectorRef,
        private readonly breakpoints: BreakpointObserver
    ) {
        this.name = this.autologoutService.val ?? 'JONSync Portal';
        this.mobile$ = this.breakpoints
            .observe(['(max-width: 576px)'])
            .pipe(map(({ matches }) => matches));
    }

    onCreatedSideBar(): void {
        this.mobile$?.pipe(
            tap(isMobile => {
                if (this.sidebarTreeviewInstance) {
                    this.sidebarTreeviewInstance.isOpen = !isMobile;
                }
            })
        );

        if (!this.currentUser) {
            this.sidebarTreeviewInstance?.destroy();
        }
    }

    onClick(e: any): void {
        e.clickToClose = true;
    }

    onBeforeOpen(e: any): void {
        const progress = e.element.querySelector('.e-toast-progress');

        progress.style.height = '5px';
        progress.style.backgroundColor = 'White';
    }

    ngOnInit(): void {
        this.titleService.init();

        this.router.events.pipe(untilDestroyed(this)).subscribe((route: Event) => {
            if (route instanceof Scroll) {
                this.currentUrl = route.routerEvent.url;
            } else if (route instanceof RouterEvent) {
                this.currentUrl = route.url;
            }

            this.specialPage = this.specialPages.includes(this.currentUrl);
        });

        this.messageService.notifs$
            .pipe(untilDestroyed(this))
            .subscribe((notification: NotificationMessage | null) => {
                if (notification) {
                    const notifObj = {
                        title: notification.title,
                        content: notification.body,
                        cssClass:
                            notification.type === 'error'
                                ? 'e-toast-danger'
                                : notification.type === 'warning'
                                  ? 'e-toast-warning'
                                  : notification.type === 'info'
                                    ? 'e-toast-info'
                                    : notification.type === 'success'
                                      ? 'e-toast-success'
                                      : 'e-toast-primary',
                    };

                    this.notification = notification;
                    this.notifElement.show(notifObj);
                }
            });

        this.authenticationService.currentUser$
            .pipe(untilDestroyed(this))
            .subscribe((user: User | null | undefined) => {
                if (user?.id) {
                    this.currentUser = user;
                    this.isLogedIn = true;
                    this.timeoutId = setInterval(() => {
                        if (this.currentUser) {
                            if (this.environmentService.versionURL) {
                                this.versionCheckService.checkVersion(
                                    this.environmentService.versionURL as string
                                );
                            }

                            this.autologoutService.isOpen
                                .pipe(untilDestroyed(this))
                                .subscribe((isOpen: boolean) => {
                                    if (isOpen) {
                                        this.autoLogoutDialog?.show();
                                        this.autoLogoutDialogState = true;
                                    } else {
                                        this.autoLogoutDialog?.hide();
                                        this.autoLogoutDialogState = false;
                                    }
                                });
                        }
                    }, 120000);

                    this.changeDetector.markForCheck();
                } else {
                    this.isLogedIn = false;
                    this.currentUser = null;
                    this.changeDetector.markForCheck();
                    clearInterval(this.timeoutId);
                }
            });
    }

    // TODO copy this approach for ALL EJ-DIALOGS
    ngAfterViewInit(): void {
        document.onclick = (args: any): void => {
            const currentTarget = args.target.closest('ejs-dialog, button, .nav-link');

            if (this.notifDialog?.visible && currentTarget === null) {
                this.notifDialog.hide();
            }
        };
    }

    ngOnDestroy(): void {
        if (this.timeoutId) {
            clearInterval(this.timeoutId);
        }

        // Call destroy on services to clean up subscriptions
        this.autologoutService.destroy();
    }

    // eslint-disable-next-line complexity
    onCreateMenu(): void {
        // SELECT AND EXPAND ITEM MENU AFTER NEW RELOAD BASED ON ANGULAR ROUTE
        if (this.currentUser) {
            if (typeof this.treeMenuInstance !== 'undefined') {
                if (this.location.isCurrentPathEqualTo('/')) {
                    this.treeMenuInstance.selectedNodes = ['01'];
                }

                if (this.location.isCurrentPathEqualTo('/compare/')) {
                    this.treeMenuInstance.selectedNodes = ['02'];
                }

                if (this.location.isCurrentPathEqualTo('/import/')) {
                    this.treeMenuInstance.selectedNodes = ['03'];
                }

                if (this.location.isCurrentPathEqualTo('/export/')) {
                    this.treeMenuInstance.selectedNodes = ['04'];
                }

                if (this.location.isCurrentPathEqualTo('/report/')) {
                    this.treeMenuInstance.selectedNodes = ['05'];
                }

                // APPLY ACL TO THE DEF DATA SOURCE FOR MENU
                this.data = [
                    {
                        nodeId: '01',
                        nodeText: 'Nástěnka',
                        iconCss: 'fa-light fa-tachometer-alt',
                        url: '/',
                        acl: true,
                    },
                    {
                        nodeId: '02',
                        nodeText: 'Porovnání',
                        iconCss: 'fa-light fa-ballot-check',
                        badge: 'e-badge e-badge-secondary float-right',
                        url: '/compare/',
                        acl: true,
                    },
                    {
                        nodeId: '03',
                        nodeText: 'Import',
                        iconCss: 'fa-light fa-headset',
                        badge: 'e-badge e-badge-secondary float-right',
                        url: '/import/',
                        acl: true,
                    },
                    {
                        nodeId: '04',
                        nodeText: 'Export',
                        iconCss: 'fa-light fa-calendar-alt',
                        badge: 'e-badge e-badge-secondary float-right',
                        url: '/export/',
                        acl: true,
                    },
                    {
                        nodeId: '05',
                        nodeText: 'Report',
                        iconCss: 'fa-light fa-list-alt',
                        badge: 'e-badge e-badge-secondary float-right',
                        url: '/report/',
                        acl: true,
                    },
                ];

                this.data = this.data.filter((x: NavData) => {
                    if (x.nodeChild && x.nodeChild.length > 0) {
                        x.nodeChild = x.nodeChild.filter(y => y.acl);
                    }

                    return x.acl;
                });

                this.treeMenuInstance.fields.dataSource = this.data as unknown as {
                    [key: string]: object;
                }[];
            }
        }
    }

    getUserIcon(): void {
        if (this.userMenuIcon && this.currentUser) {
            // eslint-disable-next-line max-len
            this.userMenuIcon.content = `<img alt="profilový obrázek" class="img-fluid rounded-circle z-depth-0 mr-1" src="assets/img${this.currentUser.personalphoto}" style="height: 30px">${this.currentUser.fullname}`;
        }
    }

    loadRoutingContent(args: NodeClickEventArgs): void {
        if (typeof this.treeMenuInstance !== 'undefined') {
            if (args.node.classList.contains('e-level-1')) {
                this.treeMenuInstance.collapseAll();
                this.treeMenuInstance.expandAll([args.node]);
                this.treeMenuInstance.expandOn = 'None';
            }

            const data: { [key: string]: object | string }[] = this.treeMenuInstance.getTreeData(
                args.node
            );

            if (typeof data[0].url !== 'undefined') {
                const routerLink: string = data[0].url as string;

                void this.router.navigate([routerLink]);
            }
        }
    }

    openClick(): void {
        this.sidebarTreeviewInstance?.toggle();

        if (this.sidebarTreeviewInstance?.isOpen) {
            this.navHandlerService.publish('open');
        } else {
            this.navHandlerService.publish('close');
        }
    }

    /* closeDocumentClick(): void {
     if (this.sidebarTreeviewInstance?.isOpen) {
     this.sidebarTreeviewInstance.toggle();
     this.navHandlerService.publish('open');
     }
     else {
     this.navHandlerService.publish('close');
     }
     }*/

    selectUserMenu(args: MenuEventArgs): void {
        if (args.item.text === 'Profil') {
            void this.router.navigate(['/users/detail', this.currentUser?.id]);
        }

        if (args.item.text === 'Odhlásit') {
            this.logout();
        }
    }

    startSearch(): void {
        if (this.fullTextSearchObj && this.fullTextSearchObj.value.length >= 3) {
            this.renderSearchResultDialog = true;
            this.searchQuery = this.fullTextSearchObj.value;
        }
    }

    goBack(): void {
        this.location.back();
    }

    logout(): void {
        this.isLogedIn = false;
        clearInterval(this.timeoutId);
        this.treeMenuInstance?.destroy();
        this.sidebarTreeviewInstance?.hide();
        this.logoutService.logout();
    }
}
